/**
 * Carte de bon cadeau unifiée avec support multi-modes
 *
 * Composant réutilisable affichant un bon cadeau avec différents modes d'interaction :
 * - Mode "catalog" : Affichage standard pour la vue liste publique (carte cliquable)
 * - Mode "voucherBuilder" : Affichage pour la composition de bons personnalisés (sélection)
 *
 * Compatible avec les deux formats de données backend :
 * - Nouveau format : amount.type, amount.value, display.imageUrl
 * - Ancien format : amountType, fixedAmount, imagePath
 *
 * @module GiftVoucherCard
 */

import React from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardMedia,
  Typography,
  Button,
  Stack,
  Chip,
  alpha,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {eCurrency} from 'packages/common/utils/formatNumber';
import {getContrastTextColor} from 'packages/common/utils/colorUtils';
import {useVoucherTranslation} from 'packages/common/utils/multiLanguageHelper';
import {getDisplayData} from 'packages/common/utils/variantDisplayHelper';
import {getHtmlVisibleText} from 'packages/common/utils/htmlText';
import {VariantImageCarousel} from './VariantImageCarousel';

const TEXT_LENGTHS = {
  TITLE: 80,
  DESCRIPTION: 200,
};

/**
 * Configuration des modes d'affichage de la carte
 *
 * @constant {Object} CARD_MODES
 * @property {Object} catalog - Mode catalogue public standard
 * @property {Object} voucherBuilder - Mode construction de bon personnalisé
 */
const CARD_MODES = {
  catalog: {
    actionIcon: CardGiftcardIcon,
    actionLabel: 'giftVoucher.card.offer',
    actionLabelDefault: 'Offrir',
    showDetailsButton: true,
    showHighlightBadge: true,
    clickable: true,
  },
  voucherBuilder: {
    actionIcon: AddShoppingCartIcon,
    actionLabel: 'customPack.add',
    actionLabelDefault: 'Ajouter au pack',
    actionLabelSelected: 'customPack.addAgain',
    actionLabelSelectedDefault: 'Ajouter à nouveau',
    showDetailsButton: true,
    showHighlightBadge: true,
    clickable: false,
    selectedIcon: CheckCircleIcon,
    selectedBadgeLabel: 'customPack.inPack',
    selectedBadgeLabelDefault: 'Dans le pack',
  },
};

const DEFAULT_VOUCHER_IMAGE = 'assets/images/giftVouchersTypes/default/giftvoucher_default.webp';

const truncateText = (text, maxLength, endChar = '...') => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  const truncated = text.substr(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  return `${truncated.substr(0, lastSpace)}${endChar}`;
};

// Une fin de paragraphe devient un espace : sans lui, « rencontrent.</p><p>Profitez »
// se lirait « rencontrent.Profitez » sur la carte.
const stripAndTruncate = (html, maxLength) => truncateText(getHtmlVisibleText(html), maxLength);

/**
 * Carte de bon cadeau unifiée avec support multi-modes
 *
 * @param {Object} props - Props du composant
 * @param {Object} props.companyDetails - Détails de l'établissement
 * @param {Object} props.voucher - Données du bon cadeau à afficher
 * @param {string} props.color - Couleur principale de l'établissement
 * @param {Function} props.onNavigate - Callback de navigation déclenché au clic (reçoit le voucher en argument)
 * @param {Function} props.onOpenDescription - Callback d'ouverture de la description complète
 * @param {Array} props.categories - Liste des catégories disponibles
 * @param {string} props.mode - Mode d'affichage ('catalog' | 'voucherBuilder')
 * @param {string} props.displayMode - Mode d'affichage layout ('fixed' | 'floating') pour ajustements widget
 * @param {Function} props.onAction - Callback d'action principale (achat ou ajout au bon)
 * @param {boolean} props.isSelected - État de sélection (mode voucherBuilder uniquement)
 * @return {JSX.Element} Carte de bon cadeau
 */
export function GiftVoucherCard({
  companyDetails,
  voucher,
  color,
  onNavigate,
  onOpenDescription,
  categories,
  mode = 'catalog',
  displayMode = 'fixed',
  onAction,
  isSelected = false,
}) {
  const {t} = useTranslation();
  const cdnEndpoint = process.env.REACT_APP_SPACES_CDN_ENDPOINT || 'https://files.brydge.app';
  const isFloating = displayMode === 'floating';

  const {getTranslatedName, getTranslatedDescription} = useVoucherTranslation(companyDetails);
  const modeConfig = CARD_MODES[mode];

  const getImageUrl = (imagePath) => {
    if (imagePath?.startsWith('http://') || imagePath?.startsWith('https://')) {
      return imagePath;
    }
    const cleanPath = imagePath ? (imagePath.startsWith('/') ? imagePath.slice(1) : imagePath) : DEFAULT_VOUCHER_IMAGE;
    return `${cdnEndpoint}/${cleanPath}`;
  };

  const handleCardClick = () => {
    if (!modeConfig.clickable) return;
    if (onNavigate) onNavigate(voucher);
  };

  const handleKeyDown = (event) => {
    if (!modeConfig.clickable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick();
    }
  };

  const handleActionClick = (e) => {
    if (mode === 'voucherBuilder') {
      e.stopPropagation();
    }
    if (onAction) {
      onAction(voucher);
    } else if (mode === 'catalog') {
      handleCardClick();
    }
  };

  // Récupération des données d'affichage avec fallback
  const displayData = React.useMemo(() => {
    return getDisplayData(voucher, null);
  }, [voucher]);

  // Résolution de l'image : nouveau format (display.imageUrl) ou ancien format (imagePath via getDisplayData)
  const resolvedImagePath = voucher.display?.imageUrl || displayData.imagePath;

  // Résolution de isHighlighted dans les deux formats
  const isHighlighted = voucher.display?.isHighlighted || voucher.isHighlighted;

  const translatedName = getTranslatedName(voucher);
  const translatedDescription = getTranslatedDescription(voucher);

  const formattedTitle = truncateText(translatedName, TEXT_LENGTHS.TITLE);
  const formattedDescription = stripAndTruncate(
      voucher.shortDescription || translatedDescription,
      TEXT_LENGTHS.DESCRIPTION,
  );

  // Prix formatté - support des deux formats de données
  const formattedPrice = (() => {
    // Nouveau format (amount.type)
    if (voucher.amount) {
      if (voucher.amount.type === 'fixed') {
        return eCurrency(voucher.amount.value);
      }
      if (voucher.amount.type === 'matrix' && voucher.amount.pricingMatrix?.length > 0) {
        const availableEntries = voucher.amount.pricingMatrix.filter((e) => e.isAvailable !== false);
        if (availableEntries.length > 0) {
          const minAmount = Math.min(...availableEntries.map((e) => e.price));
          return `${t('common.giftVoucher.card.fromPrice')} ${eCurrency(minAmount)}`;
        }
      }
      return t('common.giftVoucher.card.freeAmount');
    }

    // Ancien format (amountType)
    if (voucher.amountType === 'fixed') {
      return eCurrency(voucher.fixedAmount);
    }
    if (voucher.amountType === 'matrix' && voucher.variantConfig?.pricingMatrix?.length > 0) {
      const availableEntries = voucher.variantConfig.pricingMatrix.filter((e) => e.isAvailable !== false);
      if (availableEntries.length > 0) {
        const minAmount = Math.min(...availableEntries.map((e) => e.price));
        return `${t('common.giftVoucher.card.fromPrice')} ${eCurrency(minAmount)}`;
      }
    }
    return t('common.giftVoucher.card.freeAmount');
  })();

  // Un bon à montant libre n'a pas de prix catalogue fixe : le client choisit son montant.
  // Dans ce cas, on conserve le label "Montant libre" même si une promo est active
  // (la remise / le bonus seront calculés à l'étape de personnalisation).
  const isFreeAmountVoucher = (() => {
    const type = voucher.amount?.type || voucher.amountType;
    return type !== 'fixed' && type !== 'matrix';
  })();

  // Détection des images de variantes (support des deux formats)
  const hasVariantImages = React.useMemo(() => {
    const axes = voucher.amount?.axes || voucher.variantConfig?.axes;
    const isMatrix = voucher.amount?.type === 'matrix' || voucher.amountType === 'matrix';
    return isMatrix && axes?.some((axis) =>
      axis.valueType === 'string' &&
      axis.values?.some((v) => v.variantImagePath && v.isActive),
    );
  }, [voucher]);

  const ActionIcon = modeConfig.actionIcon;
  const SelectedIcon = modeConfig.selectedIcon;

  const CategoryBadges = ({categories: cats, voucher: v, color: c}) => {
    const {getTranslatedName: getTranslatedCategoryName} = useVoucherTranslation(companyDetails);

    const voucherCategories = React.useMemo(() => {
      if (!v.categories || !cats) return [];
      return v.categories
          .map((catId) => cats.find((cat) => cat._id === catId || cat.id === catId))
          .filter(Boolean);
    }, [v.categories, cats]);

    if (voucherCategories.length === 0) return null;

    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 2,
        }}
      >
        {voucherCategories.slice(0, 1).map((category) => (
          <Box
            key={category._id || category.id}
            sx={{
              bgcolor: '#FFFFFF',
              color: c,
              py: 0.5,
              px: 2,
              borderRadius: '10px',
              fontSize: '0.875rem',
              fontWeight: 600,
              backdropFilter: 'blur(8px)',
              border: `1px solid ${alpha('#FFFFFF', 0.2)}`,
            }}
          >
            {getTranslatedCategoryName(category)}
          </Box>
        ))}

        {voucherCategories.length > 1 && (
          <Box
            sx={{
              'bgcolor': alpha('#FFFFFF', 0.9),
              'color': c,
              'py': 0.5,
              'px': 2,
              'borderRadius': '10px',
              'fontSize': '0.875rem',
              'fontWeight': 600,
              'backdropFilter': 'blur(8px)',
              'border': `1px solid ${alpha('#FFFFFF', 0.2)}`,
              'cursor': 'pointer',
              'position': 'relative',
              '&:hover': {
                'bgcolor': '#FFFFFF',
                '& .more-categories': {
                  display: {md: 'block'},
                },
              },
            }}
          >
            +{voucherCategories.length - 1}

            <Box
              className="more-categories"
              sx={{
                'display': 'none',
                'position': 'absolute',
                'top': '100%',
                'left': 0,
                'mt': 1,
                'p': 1,
                'bgcolor': 'white',
                'borderRadius': 2,
                'boxShadow': `0 4px 12px ${alpha(c, 0.15)}`,
                'minWidth': 150,
                'zIndex': 10,
              }}
            >
              {voucherCategories.slice(1).map((category) => (
                <Typography
                  key={category._id || category.id}
                  sx={{
                    p: 1,
                    fontSize: '0.875rem',
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getTranslatedCategoryName(category)}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <Box
      role={modeConfig.clickable ? 'button' : undefined}
      tabIndex={modeConfig.clickable ? 0 : undefined}
      aria-label={modeConfig.clickable ? `${t('giftVoucher.card.offer', 'Offrir')} ${translatedName}, ${formattedPrice}` : undefined}
      sx={{
        'position': 'relative',
        'width': '100%',
        'maxWidth': 450,
        'cursor': modeConfig.clickable ? 'pointer' : 'default',
        '&:focus': {outline: 'none'},
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: color,
          outlineOffset: '4px',
          borderRadius: '12px',
        },
      }}
      onClick={modeConfig.clickable ? handleCardClick : undefined}
      onKeyDown={modeConfig.clickable ? handleKeyDown : undefined}
    >
      {/* Badge Coup de Coeur */}
      {modeConfig.showHighlightBadge && isHighlighted && (
        <Box
          sx={{
            'position': 'absolute',
            'top': -12,
            'right': -12,
            'width': 48,
            'height': 48,
            'borderRadius': '50%',
            'bgcolor': 'white',
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'boxShadow': `0 4px 12px ${alpha(color, 0.2)}`,
            'zIndex': 3,
            'backdropFilter': 'blur(8px)',
            'border': `2px solid ${alpha(color, 0.1)}`,
            'animation': 'float 3s ease-in-out infinite',
            '@keyframes float': {
              '0%': {transform: 'translateY(0px)'},
              '50%': {transform: 'translateY(-4px)'},
              '100%': {transform: 'translateY(0px)'},
            },
          }}
        >
          <FavoriteIcon
            aria-hidden="true"
            sx={{
              'color': 'error.main',
              'fontSize': 28,
              'animation': 'pulse 1.5s infinite',
              '@keyframes pulse': {
                '0%': {transform: 'scale(1)'},
                '50%': {transform: 'scale(1.15)'},
                '100%': {transform: 'scale(1)'},
              },
            }}
          />
        </Box>
      )}

      <Card
        sx={{
          'width': '100%',
          'height': 'auto',
          'borderRadius': 3,
          'bgcolor': 'background.paper',
          'position': 'relative',
          'overflow': 'hidden',
          'boxShadow': mode === 'voucherBuilder' && isSelected ?
            `0 12px 32px ${alpha(color, 0.35)}` :
            `0 8px 24px ${alpha(color, 0.15)}`,
          'transition': 'all 0.3s ease',
          'display': 'flex',
          'flexDirection': 'column',
          'border': mode === 'voucherBuilder' && isSelected ?
            `3px solid ${color}` :
            '3px solid transparent',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 12px 32px ${alpha(color, 0.25)}`,
          },
        }}
      >
        {/* Section Image + Badges */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: isFloating ? '45%' : '56.25%',
            flexShrink: 0,
          }}
        >
          {hasVariantImages ? (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            >
              <VariantImageCarousel
                giftVoucherType={voucher}
                rotationInterval={3500}
              />
            </Box>
          ) : (
            <CardMedia
              component="img"
              image={getImageUrl(resolvedImagePath)}
              alt={translatedName}
              onError={(e) => {
                e.target.src = `${cdnEndpoint}/${DEFAULT_VOUCHER_IMAGE}`;
              }}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '12px 12px 0 0',
              }}
            />
          )}

          {/* Overlay gradient */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(to bottom,
                transparent 0%,
                ${alpha('#000', 0.2)} 70%,
                ${alpha('#000', 0.6)} 100%
              )`,
              zIndex: 1,
            }}
          />

          {/* Badge Promo (remise ou bonus de valeur selon promotionEffect) */}
          {voucher.activePromotion?.hasDiscount && (
            <Chip
              label={(() => {
                const isBonus = voucher.activePromotion.promotionEffect === 'bonus_value';
                const value = voucher.activePromotion.discountType === 'percentage' ?
                  `${voucher.activePromotion.discountValue}%` :
                  eCurrency(voucher.activePromotion.discountValue);
                return isBonus ?
                  t('giftVoucher.catalog.bonusBadge', {value}) :
                  t('giftVoucher.catalog.discountBadge', {value});
              })()}
              sx={{
                position: 'absolute',
                bottom: -16,
                left: 16,
                // Vert sobre pour les bonus, rouge pour les remises — couleurs centralisées dans le thème
                bgcolor: (theme) => voucher.activePromotion.promotionEffect === 'bonus_value' ?
                  theme.palette.promotion.bonus :
                  theme.palette.error.main,
                color: 'white',
                fontWeight: 'bold',
                zIndex: 2,
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              }}
            />
          )}

          {/* Badge Catégorie - Mode Catalog uniquement */}
          {mode === 'catalog' && (
            <CategoryBadges
              categories={categories}
              voucher={voucher}
              color={color}
            />
          )}

          {/* Badge "Dans le bon" - Mode Voucher Builder uniquement */}
          {mode === 'voucherBuilder' && isSelected && SelectedIcon && (
            <Chip
              icon={<SelectedIcon sx={{color: 'white !important', fontSize: 20}} />}
              label={t(modeConfig.selectedBadgeLabel, modeConfig.selectedBadgeLabelDefault)}
              sx={{
                'position': 'absolute',
                'top': 16,
                'right': 16,
                'bgcolor': color,
                'color': 'white',
                'fontWeight': 700,
                'fontSize': '0.85rem',
                'zIndex': 2,
                'boxShadow': `0 4px 12px ${alpha(color, 0.4)}`,
                '& .MuiChip-icon': {
                  marginLeft: '8px',
                },
              }}
            />
          )}

          {/* Badge Prix */}
          <Box
            sx={{
              position: 'absolute',
              bottom: -16,
              right: 16,
              zIndex: 2,
              bgcolor: color,
              color: getContrastTextColor(color),
              py: 1,
              px: 2,
              borderRadius: 2,
              boxShadow: `0 4px 12px ${alpha(color, 0.3)}`,
            }}
          >
            {voucher.activePromotion?.hasDiscount &&
              voucher.activePromotion?.promotionEffect !== 'bonus_value' &&
              !isFreeAmountVoucher ? (
              // Remise classique sur prix catalogue fixe/matrix : prix barré + prix remisé
              <Stack direction="row" alignItems="center" spacing={1}>
                {(voucher.amountType === 'matrix' || voucher.amount?.type === 'matrix') && (
                  <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                    {t('common.giftVoucher.card.fromPrice')}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    textDecoration: 'line-through',
                    opacity: 0.7,
                  }}
                >
                  {eCurrency(voucher.activePromotion.originalPrice)}
                </Typography>
                <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                  {eCurrency(voucher.activePromotion.discountedPrice)}
                </Typography>
              </Stack>
            ) : (
              // Bonus, montant libre, ou pas de promo : on garde le libellé catalogue.
              // Pour un montant libre, la promo s'applique à l'étape de personnalisation.
              <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                {formattedPrice}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Section Contenu */}
        <Box
          sx={{
            p: 2,
            pt: isFloating ? 3.5 : 6,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                'height': '2.4em',
                'marginBottom': 2,
                'position': 'relative',
                'display': 'flex',
                'alignItems': 'center',
                'paddingBottom': isFloating ? '16px' : '24px',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: 60,
                  height: '2px',
                  background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.2)} 100%)`,
                  transition: 'width 0.3s ease',
                },
                '&:hover::after': {
                  width: 100,
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.2,
                  letterSpacing: '0.02em',
                  color: 'text.primary',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  width: '100%',
                }}
                title={translatedName}
              >
                {formattedTitle}
              </Typography>
            </Box>

            <Box
              sx={{
                minHeight: isFloating ? '48px' : '90px',
              }}
            >
              <Box
                sx={{
                  'display': 'flex',
                  'alignItems': 'center',
                  'py': 1,
                  'position': 'relative',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: isFloating ? 2 : 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    width: '100%',
                  }}
                  title={stripAndTruncate(voucher.shortDescription || translatedDescription, 500)}
                >
                  {formattedDescription}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Boutons */}
          <Stack
            direction="row"
            spacing={1}
            sx={{mt: isFloating ? 1.5 : 2}}
          >
            <Button
              fullWidth
              size="medium"
              tabIndex={-1}
              startIcon={<ActionIcon aria-hidden="true" />}
              variant={mode === 'voucherBuilder' && isSelected ? 'outlined' : 'contained'}
              sx={mode === 'voucherBuilder' && isSelected ? {
                'borderColor': color,
                'color': color,
                'py': 1,
                'textTransform': 'none',
                'borderRadius': 2,
                'fontWeight': 600,
                'fontSize': '0.9rem',
                'borderWidth': '2px',
                'transition': 'all 0.2s ease',
                '&:hover': {
                  borderColor: color,
                  bgcolor: alpha(color, 0.08),
                  borderWidth: '2px',
                },
              } : {
                'bgcolor': color,
                'color': getContrastTextColor(color),
                'py': 1,
                'textTransform': 'none',
                'borderRadius': 2,
                'fontWeight': 600,
                'fontSize': '0.9rem',
                'boxShadow': `0 4px 12px ${alpha(color, 0.2)}`,
                'transition': 'all 0.2s ease',
                '&:hover': {
                  bgcolor: alpha(color, 0.9),
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 16px ${alpha(color, 0.3)}`,
                },
              }}
              onClick={handleActionClick}
            >
              {t(
                isSelected ? modeConfig.actionLabelSelected || modeConfig.actionLabel : modeConfig.actionLabel,
                isSelected ? modeConfig.actionLabelSelectedDefault || modeConfig.actionLabelDefault : modeConfig.actionLabelDefault,
              )}
            </Button>

            {modeConfig.showDetailsButton && translatedDescription && onOpenDescription && (
              <Button
                size="medium"
                tabIndex={-1}
                variant="outlined"
                startIcon={<InfoOutlinedIcon aria-hidden="true" />}
                sx={{
                  'py': 1,
                  'width': '30%',
                  'borderColor': alpha(color, 0.5),
                  'color': color,
                  'textTransform': 'none',
                  'fontWeight': 600,
                  'fontSize': '0.9rem',
                  'position': 'relative',
                  'zIndex': 10,
                  'flexShrink': 0,
                  '&:hover': {
                    borderColor: color,
                    bgcolor: alpha(color, 0.05),
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenDescription(translatedDescription);
                }}
              >
                {t('giftVoucher.card.details', 'Détails')}
              </Button>
            )}
          </Stack>
        </Box>
      </Card>
    </Box>
  );
}

GiftVoucherCard.propTypes = {
  companyDetails: PropTypes.object.isRequired,
  voucher: PropTypes.object.isRequired,
  color: PropTypes.string.isRequired,
  onNavigate: PropTypes.func,
  onOpenDescription: PropTypes.func,
  categories: PropTypes.array,
  mode: PropTypes.oneOf(['catalog', 'voucherBuilder']),
  displayMode: PropTypes.oneOf(['fixed', 'floating']),
  onAction: PropTypes.func,
  isSelected: PropTypes.bool,
};

export default GiftVoucherCard;
